/**
 * Step ids for the v2 GitLab installation wizard.
 * Kept separate from the v1 dialog's STEP_IDS so the two wizards never collide.
 */
export const STEP_IDS = {
  CONNECT: 'connect',
  SELECT: 'select',
  INSTALL: 'install',
} as const;

export const FORM_IDS = {
  CONNECT: 'gitlab-v2-connect-form',
} as const;

/** Select-step per-page selection reducer actions. */
export const SELECTION_ACTIONS = {
  TOGGLE_GROUP: 'toggle-group',
  TOGGLE_PROJECT: 'toggle-project',
  PRESELECT: 'preselect',
  DESELECT: 'deselect',
  RESET: 'reset',
} as const;

export const CONNECT_FIELDS = {
  GITLAB_URL: 'gitlabUrl',
  ACCESS_TOKEN: 'accessToken',
} as const;

/**
 * Typed connect-error codes the BE returns (contract §1). The Connect step maps
 * each to reason-specific i18n copy; anything else falls back to generic copy.
 */
export const CONNECT_ERROR_CODES = [
  'invalid_token',
  'insufficient_scope',
  'host_unreachable',
  'group_not_found',
  'not_a_group',
  'invalid_url',
  'gitlab_id_already_claimed',
  'requires_instance_admin',
] as const;

/**
 * Typed listing-error codes the Select step renders reason-specific copy for: a
 * dead/under-scoped PAT or a browsed scope gone while paging (422 typed envelope).
 * A subset of the connect codes, but the Select copy differs — a not-found here
 * means "go back and pick another," not "fix the URL." Any other failure (a
 * transient 502) keeps the generic message + Retry.
 */
export const SELECT_ERROR_CODES = [
  'invalid_token',
  'insufficient_scope',
  'group_not_found',
] as const;

/**
 * Typed provision-failure codes surfaced on a `failed` status (contract §3).
 */
export const PROVISION_ERROR_CODES = [
  'identity_unverified',
  'not_group_owner',
  'not_admin',
  'insufficient_permissions',
  'bot_creation_failed',
  'gitlab_id_already_claimed',
] as const;

/**
 * The "already connected to another Qodo workspace" claim conflict. Arrives from
 * connect (checked before provisioning) or, on a race, from provision — same
 * code. Its copy is host-aware: cloud claims the top-level group, self-hosted the
 * whole instance.
 */
export const GITLAB_ID_ALREADY_CLAIMED_CODE = 'gitlab_id_already_claimed';

/** Per-item install-failure codes surfaced in install results (contract §4). */
export const INSTALL_ERROR_CODES = [
  'not_owner',
  'not_maintainer',
  'insufficient_permissions',
  // A transient hook-creation failure (5xx / network), so retry is genuinely right.
  'hook_creation_failed',
  // GitLab refused the webhook itself (an unmapped 4xx). Retry repeats the refusal.
  'hook_creation_rejected',
  // The bot couldn't be granted membership on the scope's group (retryable).
  'member_add_failed',
  // The parent group's membership lock refuses the bot as a project member (405).
  // A capability switch, not a permission check, so no token and no retry can clear
  // it — only a group Owner turning the lock off.
  'member_add_not_allowed',
  // A selected scope was deleted or became invisible to the token between Select
  // and install (404). Distinct from hook_creation_failed: re-select it and retry.
  'scope_not_found',
  // An already-hooked repo can't be adopted into this install (no backing config
  // resolves — missing, or owned by another/deactivated install). Terminal.
  'config_adoption_failed',
  // Whole-run failures (PAT re-validated up front for install): a bad/insufficient
  // token, or a transient GitLab/network failure during that check (retryable).
  'invalid_token',
  'insufficient_scope',
  'upstream_error',
] as const;

/**
 * Per-scope manage-failure codes (add + remove branches). Superset of
 * INSTALL_ERROR_CODES with the remove-side codes: a removed hook can fail to
 * delete, the bot can fail to leave, or the scope is covered by a group hook
 * (defensive — the FE renders such scopes read-only, so this is a backstop).
 */
export const MANAGE_RESULT_ERROR_CODES = [
  'not_owner',
  'not_maintainer',
  'insufficient_permissions',
  'hook_creation_failed',
  'hook_creation_rejected',
  'member_add_failed',
  'member_add_not_allowed',
  'hook_deletion_failed',
  'member_remove_failed',
  'group_tracked',
  'config_adoption_failed',
] as const;

/**
 * Typed token errors the stateless list/manage calls return for a bad or
 * under-scoped PAT (422 with `detail.code`). Manage surfaces these at the PAT
 * field so the user can fix the token in place.
 */
export const GITLAB_PAT_ERROR_CODES = [
  'invalid_token',
  'insufficient_scope',
] as const;

/**
 * Whole-run manage/uninstall failure codes (contract §1) — surfaced on a `failed`
 * status poll, not per-scope. The PAT codes point back at the token;
 * `upstream_error` is a transient GitLab/network failure during the run and is
 * retryable (not a bad token).
 */
export const MANAGE_WHOLE_RUN_ERROR_CODES = [
  'invalid_token',
  'insufficient_scope',
  'upstream_error',
] as const;

/** i18n key suffix for the generic connect/provision failure fallback. */
export const GENERIC_PROVISIONING_ERROR_KEY = 'generic';

/**
 * i18n key suffix for a lost provisioning session (provision 409 / status 404):
 * the transient Connect session expired and the user must reconnect.
 */
export const SESSION_EXPIRED_ERROR_KEY = 'session_expired';
